import { Flex, Tabs } from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useRef, useState } from "react";
import { APDashboard } from "../../components/Assessment/APDashboard/APDashboard";
import { Card } from "../../components/Card";
import {
  ERROR,
  useNotification,
} from "../../components/CustomHooks/NotificationProvider";
import Filter from "../../components/Filter/Filter";
import ChartData from "../../sections/ChartData";
import { AssessmentPlanService } from "../../services/AssessmentService";
import { SubjectService } from "../../services/SubjectService";
import { TermsContext, UserDetailContext } from "../../utils/Context";
import { CurriculumMap } from "../CurriculumMap";

const ACPC = ({
  dashboardFilters,
  setDashboardFilters,
  trigger,
  setTrigger,
  linkDetails,
  subjects,
}) => {
  const userDetails = useContext(UserDetailContext);
  const showNotification = useNotification();
  const { canvasTerms } = useContext(TermsContext);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [assessmentPlanList, setAssessmentPlanList] = useState([]);
  const [selectedAssessmentPlan, setSelectedAssessmentPlan] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState({});
  const [loadedCycles, setLoadedCycles] = useState(false);
  const [planSubjects, setPlanSubjects] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const fetchAssessmentPlans = async () => {
      try {
        if (userDetails.isAC()) {
          const plans = await AssessmentPlanService.getLightAssessmentPlans();
          handlePageState(plans);
        } else {
          const subjects = await SubjectService.getSubjects();
          const assignedSubjects = subjects
            .filter((subject) =>
              userDetails.ac_subjects.includes(subject.title)
            )
            .map((subject) => subject.id);

          const promiseList = [
            AssessmentPlanService.getLightAssessmentPlans(),
            AssessmentPlanService.getAssessmentPlansBySubjects(
              assignedSubjects
            ),
          ];

          let plans = [];
          const [allPlans, plansBySubject] = await Promise.all(promiseList);
          plans = [];
          allPlans.forEach((plan) => {
            if (
              !plans.some((p) => p.id === plan.id) &&
              (plan.results_purl.reviewers.includes(userDetails.email) ||
                plansBySubject.some((p) => p.id === plan.id))
            ) {
              plans.push(plan);
            }
          });
          plans = plans.sort(
            (a, b) => new Date(b.date_created) - new Date(a.date_created)
          );
          handlePageState(plans);
        }
      } catch {
        showNotification(
          t("notifications.general.fetchError", { obj: "Assessment Plans" }),
          ERROR
        );
      }
    };

    if (userDetails.role) {
      fetchAssessmentPlans();
    }
  }, [userDetails]);

  const handlePageState = (plans) => {
    const cycleList = [];
    const cycleSet = new Set();
    let chosenCycle;

    setAssessmentPlanList(plans);
    plans.forEach((plan) => {
      if (!cycleSet.has(plan.cycle.id)) {
        cycleSet.add(plan.cycle.id);
        cycleList.push(plan.cycle);
      }
    });
    setCycles(cycleList);
    if (linkDetails) {
      chosenCycle = linkDetails.cycle;
    } else {
      chosenCycle = cycleList.length > 0 ? cycleList[0] : null;
    }
    setDashboardFilters({ ...dashboardFilters, cycle: chosenCycle });
    handleSetAssessmentPlan(plans, chosenCycle);
    setLoadedCycles(true);
  };

  const handleSetAssessmentPlan = (assessmentPlanList, cycle) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;

    setTrigger(true);
    if (assessmentPlanList.length > 0 && Object.keys(cycle).length > 0) {
      const plan = assessmentPlanList.find(
        (plan) => plan.cycle.id === cycle.id
      );
      setIsLoading(true);
      AssessmentPlanService.getAssessmentPlanById(plan.id, signal)
        .then((fullPlan) => {
          setSelectedAssessmentPlan(fullPlan);
          let depts = [];
          const canvas_id_terms = [];
          const assigned_subjects = userDetails.ac_subjects;
          fullPlan.sections.forEach((section) => {
            section.assessments.forEach((assessment) => {
              depts = depts.concat(assessment.subjects);
              if (assessment.assessment_type === "external") {
                if (
                  userDetails.isAC() ||
                  (userDetails.isPC() &&
                    assessment.assessors.includes(userDetails.email))
                ) {
                  for (const term_id of assessment.assessing_terms) {
                    const term = canvasTerms.find(
                      (term) => term.id === term_id
                    );
                    if (
                      new Date(term.start_at).setHours(0, 0, 0, 0) <=
                      new Date().setHours(0, 0, 0, 0)
                    ) {
                      canvas_id_terms.push(term.canvas_id);
                    }
                  }
                  for (const sub_id of assessment.subjects) {
                    assigned_subjects.push(
                      subjects.find((sub) => sub.id === sub_id).title
                    );
                  }
                }
              } else {
                const noFutureTerms = assessment.terms.filter(
                  (t) =>
                    new Date(t.start_at).setHours(0, 0, 0, 0) <=
                    new Date().setHours(0, 0, 0, 0)
                );
                canvas_id_terms.push(...noFutureTerms.map((t) => t.canvas_id));
              }
            });
          });
          setPlanSubjects(depts);
          const all_cycle_subs = subjects
            .filter((sub) => depts.includes(sub.id))
            .sort((a, b) => a - b);
          const newDefaultSub = userDetails.isAC()
            ? all_cycle_subs[0]
            : all_cycle_subs
                .filter((sub) => assigned_subjects.includes(sub.title))
                .sort((a, b) => a - b)[0];
          setDashboardFilters((prevFilters) => ({
            ...prevFilters,
            terms: [...new Set(canvas_id_terms)],
            subject: newDefaultSub?.title,
          }));
        })
        .catch((e) => {
          if (e.name !== "AbortError") {
            showNotification(
              t("notifications.general.fetchError", { obj: "Assessment Plan" }),
              ERROR
            );
          }
        });
    } else {
      setPlanSubjects(userDetails.ac_subjects);
    }
  };

  const handleTabChange = (_e, { index }) => {
    setSelectedIndex(index);
  };

  return (
    <Flex wrap="wrap" alignItems="start">
      <Flex.Item size="100%">
        <Filter
          filters={dashboardFilters}
          setFilters={setDashboardFilters}
          handleSetAssessmentPlan={handleSetAssessmentPlan}
          assessmentPlanList={assessmentPlanList}
          cycles={cycles}
          loadedCycles={loadedCycles}
        />
      </Flex.Item>

      <Flex.Item shouldGrow shouldShrink size="600px">
        <Flex wrap="wrap" alignItems="start" height="100%">
          <Flex.Item
            shouldGrow
            size="100%"
            padding="0 small"
            margin="0 0 medium 0"
          >
            <ChartData
              showBarChart
              showLineChart
              filters={dashboardFilters}
              setFilters={setDashboardFilters}
              trigger={trigger}
              setTrigger={setTrigger}
              subjects={subjects}
              planSubjects={planSubjects}
              selectedAssessmentPlan={selectedAssessmentPlan}
            />
          </Flex.Item>

          <Flex.Item
            shouldGrow
            size="100%"
            padding="0 small"
            margin="0 0 medium 0"
          >
            <Card style={{ height: "auto" }}>
              <Tabs onRequestTabChange={handleTabChange}>
                <Tabs.Panel
                  id="assessmentPlan"
                  renderTitle={t("assessment.assessmentPlan")}
                  isSelected={selectedIndex === 0}
                >
                  <APDashboard
                    userDetails={userDetails}
                    selectedAssessmentPlan={selectedAssessmentPlan}
                    cycles={cycles}
                    selectedGoal={selectedGoal}
                    setSelectedGoal={setSelectedGoal}
                    dashboardFilters={dashboardFilters}
                    isLoading={isLoading}
                    setIsLoading={setIsLoading}
                  />
                </Tabs.Panel>
                <Tabs.Panel
                  id="curriculumMap"
                  renderTitle={t("curriculumMap.headerTitle")}
                  isSelected={selectedIndex === 1}
                >
                  <>
                    {/* <Flex> */}
                    {/* <Flex.Item>
                          <Link to="/plan/curriculum">Edit Curriculum Map</Link>
                        </Flex.Item> */}
                    {/* </Flex> */}
                    {dashboardFilters.subject && (
                      <CurriculumMap
                        selectedAssessmentPlan={selectedAssessmentPlan}
                        isDashboard={true}
                      />
                    )}
                  </>
                </Tabs.Panel>
              </Tabs>
            </Card>
          </Flex.Item>
        </Flex>
      </Flex.Item>
    </Flex>
  );
};
export default ACPC;
